/**
 * Utility functions to check if evaluation cards have data to display
 *
 * Each function takes an evaluation object and returns a boolean indicating
 * whether that specific card has meaningful data to show to the user.
 *
 * These functions are used by parent components to conditionally render cards
 * when the "Display non-empty cards only" toggle is enabled.
 *
 * @param {Evaluation} evaluation - The evaluation response to check
 * @returns {boolean} True if the current card has data; false if it has no data
 */

import type { Evaluation } from '~/entities/types';

/**
 * Check if Returned Reason Codes card has data
 */
export const hasReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const taggedResults = evaluation.tagged_reason_code_results || {};
  const untaggedResults = evaluation.untagged_reason_code_results || {};
  const extraneousResults = evaluation.extraneous_reason_code_results || {};

  return (
    Object.keys(taggedResults).length > 0
    || Object.keys(untaggedResults).length > 0
    || Object.keys(extraneousResults).length > 0
  );
};

/**
 * Check if Old Returned Reason Codes card has data (pre-cutoff evaluations)
 */
export const hasOldReturnedReasonCodesData = (evaluation: Evaluation): boolean => {
  const reasonCodes = evaluation.reason_codes_results || {};
  const extraneousCodes = evaluation.extraneous_reason_codes || {};

  return (
    Object.keys(reasonCodes).length > 0
    || Object.keys(extraneousCodes).length > 0
  );
};

/**
 * Check if Data Sources card has data
 */
export const hasDataSourcesData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.summary?.services
  && Object.keys(evaluation.final_response.summary.services).length > 0
);

/**
 * Check if Device Details card has data
 */
export const hasDeviceDetailsData = (evaluation: Evaluation): boolean => !!(
  evaluation.device_details
  && Array.isArray(evaluation.device_details)
  && evaluation.device_details.length > 0
);

/**
 * Check if Custom Fields card has data
 */
export const hasCustomFieldsData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.summary?.custom_fields
  && Object.keys(evaluation.final_response.summary.custom_fields).length > 0
);

/**
 * Check if Imported Data card has data
 */
export const hasImportedData = (evaluation: Evaluation): boolean => (
  (evaluation.imported_outcome_reasons?.length || 0) > 0
  || (evaluation.imported_output_attributes?.length || 0) > 0
  || (evaluation.imported_matrix_models?.length || 0) > 0
);

/**
 * Check if Matrix Model card has data
 *
 * NOTE: CRA evaluations filter out matrix models where is_risk_score_factor=true.
 * This basic check doesn't account for that filtering. When integrating with
 * EvaluationOverviewContent, we should use the already-computed matrixModelsToDisplay
 * value instead of calling this function directly.
 *
 * TODO: Remove this note once CRA flag is deprecated.
 */
export const hasMatrixModelData = (evaluation: Evaluation): boolean => {
  const matrixModels = evaluation.final_response?.summary?.matrix_models;
  return !!(matrixModels && Object.keys(matrixModels).length > 0);
};

/**
 * Check if Fraud Risk card should display.
 *
 * Requirements:
 * - alloy_fraud_score is not null
 * - alloy_fraud_score_response exists
 * - model_control_number >= 78 (threshold for newer fraud models)
 * - Either fraud_score or feature_importance data exists
 */
const FRAUD_MODEL_CONTROL_NUMBER_THRESHOLD = 78;

export const hasFraudRiskData = (evaluation: Evaluation): boolean => {
  const fraudScore = evaluation.alloy_fraud_score;
  const fraudResponse = evaluation.alloy_fraud_score_response;

  if (fraudScore === null || !fraudResponse) {
    return false;
  }

  const modelControlNumber = parseFloat(fraudResponse.model_control_number);
  const meetsThreshold = modelControlNumber >= FRAUD_MODEL_CONTROL_NUMBER_THRESHOLD;
  const hasData = fraudResponse.fraud_score != null
    || !!fraudResponse.feature_importance;

  return meetsThreshold && hasData;
};

/**
 * Check if Diligence card has data
 */
export const hasDiligenceData = (evaluation: Evaluation): boolean => {
  const diligence = evaluation.final_response?.diligence;
  return !!(diligence && Object.keys(diligence).length > 0);
};

/**
 * Check if Found Data card has data
 */
export const hasFoundData = (evaluation: Evaluation): boolean => {
  const foundData = evaluation.final_response?.found_data;
  return !!(foundData && Object.keys(foundData).length > 0);
};

/**
 * Check if Credit Panel card has data
 */
export const hasCreditPanelData = (evaluation: Evaluation): boolean => {
  const creditData = evaluation.final_response?.diligence?.financial?.credit || [];
  return creditData.filter(c => !!(c.source)).length > 0;
};

/**
 * Check if Evaluation Notes card has data
 */
export const hasEvaluationNotes = (evaluation: Evaluation): boolean => (
  (evaluation.notes?.length || 0) > 0
);

/**
 * Check if Entity Notes card has data
 */
export const hasEntityNotes = (entityNotes: any): boolean => !!(
  entityNotes && Array.isArray(entityNotes) && entityNotes.length > 0
);

/**
 * Check if Matched Data card has data
 */
export const hasMatchedData = (evaluation: Evaluation): boolean => !!(
  evaluation.final_response?.matching
  && Object.keys(evaluation.final_response.matching).length > 0
);

/**
 * Check if Validation Errors card should show
 */
export const hasValidationErrors = (evaluation: Evaluation): boolean => !!(
  evaluation.required && Object.keys(evaluation.required).length > 0
);

/**
 * Check if Webhook Pending card should show
 */
export const hasWebhookPendingData = (evaluation: Evaluation): boolean => !!(
  evaluation.webhook_pending_services
  && Object.keys(evaluation.webhook_pending_services).length > 0
);

/**
 * Check if Partial Details (warnings) card should show
 */
export const hasPartialDetails = (evaluation: Evaluation): boolean => !!(
  evaluation.result === 'partial'
  && evaluation.final_response?.warnings
);

/**
 * List of watchlist vendor service names.
 *
 * Note: Socure and GIACT have module configurations for watchlist, but if either
 * service is configured in the workflow, assume watchlist modules are enabled.
 * Note: sentilinkv2applicationrisk is explicitly excluded (v1 only).
 */
const WATCHLIST_VENDOR_NAMES = [
  'Middesk',
  'Quantifind',
  'Sentilink',
  'CLEAR Verified',
  'GIACT Verification Services',
  'Lexis Nexis World Compliance',
  'ComplyAdvantage',
  'Lexis Nexis Instant ID',
  'Baselayer Business',
  'Socure 30',
  'Plaid Monitor',
  'Moodys Grid',
  "Moody's Grid",
] as const;


/**
 * Check if any watchlist vendor service is configured in the workflow.
 * Used to conditionally render Watchlist Matches card/tab.
 */
export const hasWatchlistServiceConfigured = (evaluation: Evaluation): boolean => {
  const services = evaluation.final_response?.summary?.services;
  if (!services) return false;

  const configuredServices = Object.keys(services);
  return WATCHLIST_VENDOR_NAMES.some(vendor => configuredServices.includes(vendor));
};

/**
 * List of credit vendor service names.
 * Used to determine if the Credit Panel should be rendered.
 */
/**
 * List of credit vendor service names (alphabetical order).
 * Used to determine if the Credit Panel should be rendered.
 *
 * Core Credit Panel services (have EntityCreditJqSelectors in Chimera):
 * - BizChex, Creditor Watch Custom Profile, Equifax, EquifaxBusinessCreditIndustryReport,
 *   Equifax OneView Consumer Credit Report, Experian, Experian Consumer Credit Profile,
 *   QualiFile, TransUnion Credit
 *
 * Additional credit/financial services from Integrated Data Products Guide.
 */
const CREDIT_VENDOR_NAMES = [
  // Core Credit Panel services
  'BizChex',
  'Creditor Watch Custom Profile',
  'Equifax',
  'Equifax OneView Consumer Credit Report',
  'EquifaxBusinessCreditIndustryReport',
  'Experian',
  'Experian Consumer Credit Profile',
  'QualiFile',
  'TransUnion Credit',
  // Additional Equifax products
  'Equifax AU Apply',
  'Equifax AU Business Trading History',
  'Equifax AU IDMatrix',
  'Equifax AU Indepth Trading History',
  'Equifax Canada',
  'Equifax Limited Consumer Report',
  'Equifax NZ',
  'Equifax Spain',
  'Equifax UK Business Search',
  'Equifax UK Gateway',
  'EquifaxCanadaEID',
  'EquifaxTWN',
  // Additional Experian products (including Clarity)
  'Clarity',
  'Clear Bank Behavior',
  'Clear Credit Attributes',
  'Clear Fraud',
  'Clear Inquiry',
  'Clear Tradeline',
  'Experian Business Credit',
  'Experian Business Profile',
  'Experian CrossCore FraudNet',
  'Experian SBFE Multisegment',
  'Experian UK Crosscore Confirmation of Payee and Bank Account Verification - Business',
  'Experian UK Crosscore Confirmation of Payee and Bank Account Verification - Person',
  'Experian UK Crosscore Identity Services KYC',
  'Experian UK Delphi Select Single Applicant',
  'Experian UK KYB Data Company',
  // Additional TransUnion products
  'LendProtect',
  'TransUnion Canada Model Report',
  'TransUnion Model Report',
  'Transunion UK Affordability Report',
  'Transunion UK TrueVision',
  // Codat products
  'Codat Assess Data Integrity',
  'Codat Bank Statements',
  'Codat CSV',
  'Codat Lending',
  'Codat Transactions',
  // Condukt products
  'Condukt Business Verification',
  'Condukt Ownership Values',
  'Condukt UBO Detect',
  // CreditSafe
  'Creditsafe Connect - Companies',
  // Enigma
  'Enigma',
  'Enigma KYB',
  // FICO products
  'FICO Liquid Credit Lookup Business',
  'FICO Liquid Credit Obtain Decision Data',
  'WorldPay FICO',
  // ID Analytics
  'ID Analytics Credit Optics',
  // Illion products
  'Illion Commercial UEFR',
  'Illion Commercial UEPP',
  'Illion Commercial XBIRO',
  'Illion Consumer Credit',
  // LexisNexis
  'LexisNexis RiskView',
  // Multipoint
  'Multipoint Verification',
  // Nova Credit
  'Nova Credit Cash Atlas',
  'NovaCredit',
  // Pave
  'PaveCashFlow',
  // Plaid
  'PlaidAssets',
  // Prism
  'Prism Data',
  // Heron Data
  'Heron Data',
] as const;

/**
 * Check if any credit vendor service is configured in the workflow.
 * Used to conditionally render Credit Panel card.
 */
export const hasCreditServiceConfigured = (evaluation: Evaluation): boolean => {
  const services = evaluation.final_response?.summary?.services;
  if (!services) return false;

  const configuredServices = Object.keys(services);
  return CREDIT_VENDOR_NAMES.some(vendor => configuredServices.includes(vendor));
};
